import { Component, Element, Event, EventEmitter, h, Host, Prop, Watch } from '@stencil/core'

import { CssClassMap } from '../../../utils/interfaces'
import { TableAlignment, TableOverflow, TableTooltipMode, TableTooltipState } from '../table-types'

/**
 * ## Design System
 *
 * O br-table-row é um subcomponente da tabela que representa uma linha da tabela, podendo conter células de conteúdo (br-table-cell) ou células de cabeçalho (br-table-header-cell).
 * Ele é utilizado para organizar e estruturar o conteúdo da tabela, permitindo a definição de propriedades como alinhamento e overflow que podem ser herdadas pelas células associadas.
 *
 * @remarks
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/table?tab=designer).
 *
 * @Slot default: Área para inclusão de células de conteúdo da tabela, br-table-header-cell e br-table-cell.
 */
@Component({
  tag: 'br-table-row',
  styleUrl: 'table-row.scss',
  shadow: true,
})
export class TableRow {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el!: HTMLBrTableRowElement

  /**
   * Define o alinhamento horizontal das células dentro da linha, permitindo a herança de alinhamento para as células filhas com base no valor definido.
   */
  @Prop({ reflect: true }) horizontalAlignment?: TableAlignment

  /**
   * Define o alinhamento vertical das células dentro da linha, permitindo a herança de alinhamento para as células filhas com base no valor definido.
   */
  @Prop({ reflect: true }) verticalAlignment?: TableAlignment

  /**
   * Define se o conteúdo dentro das células da linha deve ser truncado, permitindo a herança dessa configuração para as células filhas com base no valor definido.
   */
  @Prop({ reflect: true }) overflow?: TableOverflow

  /**
   * Define o estado semântico do tooltip nas células desta linha.
   */
  @Prop({ reflect: true }) tooltipState?: TableTooltipState

  /**
   * Define o modo de resolução do tooltip nas células desta linha.
   */
  @Prop({ reflect: true }) tooltipMode?: TableTooltipMode

  /**
   * Evento customizado emitido quando o alinhamento é alterado, permitindo que as células reajam dinamicamente a mudanças nos tokens de alinhamento.
   */
  @Event({ bubbles: true, composed: true }) brAlignmentChange!: EventEmitter<void>

  /**
   * Evento customizado emitido quando a configuração de overflow é alterada, permitindo que as células reajam dinamicamente a mudanças no token de overflow.
   */
  @Event({ bubbles: true, composed: true }) brOverflowChange!: EventEmitter<void>

  /**
   * Gatilho do ciclo de vida do componente que é chamado após o componente ser carregado, garantindo que o alinhamento inicial seja aplicado às células da linha.
   */
  componentDidLoad() {
    this.notifyAlignmentChange()
    this.notifyOverflowChange()
  }

  /**
   * Observa mudanças nos tokens de alinhamento horizontal e vertical, emitindo um evento para notificar que o alinhamento foi alterado, permitindo que as células reajam dinamicamente a essas mudanças.
   */
  @Watch('horizontalAlignment')
  @Watch('verticalAlignment')
  protected handleAlignmentChange() {
    this.notifyAlignmentChange()
  }

  /**
   * Observa mudanças no token de overflow, emitindo um evento para notificar que a configuração de overflow foi alterada, permitindo que as células reajam dinamicamente a essa mudança.
   */
  @Watch('overflow')
  protected handleOverflowChange() {
    this.notifyOverflowChange()
  }

  /**
   * Notifica que o alinhamento foi alterado, emitindo o evento customizado correspondente.
   */
  private notifyAlignmentChange() {
    this.brAlignmentChange.emit()
  }

  /**
   * Observa mudanças no token de overflow, emitindo um evento para notificar que a configuração de overflow foi alterada, permitindo que as células reajam dinamicamente a essa mudança.
   */
  private notifyOverflowChange() {
    this.brOverflowChange.emit()
  }

  /**
   * Retorna o mapa de classes base do componente.
   *
   * @returns Um objeto representando as classes CSS a serem aplicadas ao elemento da linha da tabela.
   */
  private getRowCssClassMap(): CssClassMap {
    return {
      'br-table-row': true,
    }
  }

  /**
   * Renderiza o host do componente.
   *
   * @returns JSX.Element representando o elemento host do componente, contendo a estrutura da linha da tabela.
   */
  render() {
    return (
      <Host role="row">
        <div class={this.getRowCssClassMap()} role="presentation">
          <slot></slot>
        </div>
      </Host>
    )
  }
}
